import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Heart, ThermometerSun, Plane, Landmark, Loader2, ExternalLink, Clock, Users, Info, Compass } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import ListenButton from "./ListenButton";
import ShareBadge from "./ShareBadge";
import { VERTICALS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sp_token")}` });

export default function PlacePage() {
  const { slug } = useParams();
  const { tr } = useI18n();
  const { user } = useAuth();
  const [place, setPlace] = useState(null);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPlace(null);
    setError(null);
    axios.get(`${API}/place/${encodeURIComponent(slug)}`)
      .then((r) => setPlace(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Place not found."));
  }, [slug]);

  useEffect(() => {
    if (!user || !place) return;
    axios.get(`${API}/follows`, { headers: authHeaders() })
      .then((r) => setFollowing(r.data.some((f) => f.place_name === place.name)))
      .catch(() => {});
  }, [user, place]);

  const toggleFollow = async () => {
    if (!user) return toast.info(tr("Sign in to follow places."));
    if (following) {
      await axios.delete(`${API}/follows/${encodeURIComponent(place.name)}`, { headers: authHeaders() });
      setFollowing(false);
    } else {
      await axios.post(`${API}/follows`, { place_name: place.name, place_type: place.type }, { headers: authHeaders() });
      setFollowing(true);
      toast.success(`${tr("Following")} ${place.name}`);
    }
    window.dispatchEvent(new Event("wp:follows-changed"));
  };

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <div className="text-5xl">🗺️</div>
        <h1 className="text-2xl font-extrabold text-[#0F2557] mt-4">{tr(error)}</h1>
        <Link to="/" className="mt-6 bg-[#2563EB] text-white font-bold text-sm px-6 py-3 rounded-xl">{tr("Back to the world")} →</Link>
      </div>
    );
  }
  if (!place) {
    return (
      <div data-testid="place-loading" className="min-h-[60vh] flex items-center justify-center gap-3 text-[#2563EB]">
        <Loader2 className="animate-spin" /> <span className="font-bold">{tr("Assembling this place from the geographic graph…")}</span>
      </div>
    );
  }

  if (place && place.type === "Country" && !window.location.search.includes("from=country")) {
    return <Navigate to={`/country/${slug}`} replace />;
  }

  const introText = place.intro.join(" ");
  const hasPhoto = !!place.photo?.url;
  const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");

  return (
    <div data-testid="place-page" className="pb-20">
      <div className={`relative overflow-hidden ${hasPhoto ? "py-20 lg:py-28" : "py-14 lg:py-20"}`} style={!hasPhoto ? { background: "linear-gradient(135deg, #EAF4FD 0%, #F6FAFF 45%, #E8F7F0 100%)" } : undefined}>
        {hasPhoto && (
          <>
            <img data-testid="place-photo-hero" src={place.photo.url} alt={place.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2557]/85 via-[#0F2557]/40 to-[#0F2557]/20" />
            <span className="absolute bottom-2 right-3 text-[0.55rem] font-bold text-white/60 z-10">📷 {place.photo.credit}</span>
          </>
        )}
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8">
          <Link to="/" data-testid="place-back-link" className={`inline-flex items-center gap-2 text-sm font-bold hover:gap-3 transition-all ${hasPhoto ? "text-white/90" : "text-[#2563EB]"}`}>
            <ArrowLeft size={15} /> {tr("World")}{place.country && place.country !== place.name ? ` › ${place.country}` : ""}{place.admin1 && place.admin1 !== place.name ? ` › ${place.admin1}` : ""}
          </Link>
          <div className="flex flex-wrap items-end justify-between gap-6 mt-5">
            <div>
              <span className={`text-[0.62rem] font-extrabold tracking-widest uppercase px-3 py-1 rounded-full ${hasPhoto ? "bg-white/15 text-white border border-white/30 backdrop-blur-sm" : "bg-white text-[#1D4ED8] border border-[#BFD4F2]"}`}>{tr(place.type)}</span>
              <h1 data-testid="place-title" className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 ${hasPhoto ? "text-white drop-shadow-md" : "text-[#0F2557]"}`}>
                {place.flag} {place.name}
              </h1>
              <div className={`flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm font-semibold ${hasPhoto ? "text-white/85" : "text-[#475569]"}`}>
                {place.population && <span className="flex items-center gap-1.5"><Users size={14} className={hasPhoto ? "text-white/80" : "text-[#2563EB]"} /> {place.population.toLocaleString()}</span>}
                {place.timezone && <span className="flex items-center gap-1.5"><Clock size={14} className={hasPhoto ? "text-white/80" : "text-[#0D9488]"} /> {place.timezone}</span>}
                <span className={`font-mono-wp text-xs ${hasPhoto ? "text-white/70" : "text-[#64748B]"}`}>{place.coords.lat.toFixed(3)}°, {place.coords.lon.toFixed(3)}°</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ListenButton text={`${place.name}. ${tr(place.type)}. ${introText}`} label={tr("Listen")} />
              {user && (
                <button
                  data-testid="place-follow-button"
                  onClick={toggleFollow}
                  className={`flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full border transition-all active:scale-[0.97] ${
                    following ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#DC2626]" : "bg-white border-[#86EFAC] text-[#15803D] hover:bg-[#F0FDF4]"
                  }`}
                >
                  <Heart size={14} fill={following ? "currentColor" : "none"} /> {following ? tr("Unfollow") : tr("Follow")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        {place.premium_portal && (
          <div data-testid="premium-portal-banner" className="mt-8 border border-[#86EFAC] bg-[#F0FDF4] rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-extrabold text-[#15803D]">{tr("This country has its own dedicated portal")}</div>
              <p className="text-sm text-[#3F5548] mt-1">{tr("Full local coverage, data and communities live on")} <span className="font-extrabold">{place.premium_portal}</span> — {tr("WorldPulse hosts this gateway page only.")}</p>
            </div>
            <a
              data-testid="premium-portal-link"
              href={`https://${place.premium_portal.toLowerCase()}`}
              target="_blank" rel="noreferrer"
              className="bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-sm px-6 py-3 rounded-xl flex items-center gap-2 transition-colors"
            >
              {tr("Open")} {place.premium_portal} <ExternalLink size={14} />
            </a>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-extrabold text-[#0F2557]">{tr("Overview")}</h2>
            <div data-testid="place-intro" className="mt-4 space-y-4">
              {place.intro.length ? place.intro.map((p, i) => (
                <p key={i} className="text-[0.95rem] text-[#334155] leading-[1.8]">{tr(p)}</p>
              )) : <p className="text-sm text-[#64748B]">{tr("Overview coming soon — become a contributor to write it.")}</p>}
            </div>
            {place.intro_ai && place.intro.length > 0 && (
              <p className="text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] mt-3">{tr("AI-assisted introduction — community review welcome")}</p>
            )}

            <h2 className="text-xl font-extrabold text-[#0F2557] mt-10">{tr("Community boards for this place")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-4">
              {VERTICALS.map((v) => (
                <Link key={v.n} to={`/#verticals`} data-testid="place-board-chip"
                  className="border border-[#E5EDF7] rounded-xl px-3 py-2.5 text-xs font-bold text-[#334155] hover:border-[#2563EB] hover:text-[#2563EB] hover:bg-[#F6FAFF] transition-colors text-center leading-tight">
                  {tr(v.name)}
                </Link>
              ))}
            </div>

            {place.nearby?.length > 0 && (
              <>
                <h2 className="text-xl font-extrabold text-[#0F2557] mt-10 flex items-center gap-2"><Compass size={19} className="text-[#0D9488]" /> {tr("Explore nearby")}</h2>
                <div data-testid="nearby-places-grid" className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-4">
                  {place.nearby.map((n) => (
                    <Link
                      key={n.name}
                      to={`/place/${slugify(n.name)}`}
                      data-testid="nearby-place-chip"
                      className="border border-[#E5EDF7] rounded-xl px-3 py-2.5 hover:border-[#0D9488] hover:bg-[#F0FDFA] transition-colors group"
                    >
                      <div className="text-xs font-bold text-[#0F2557] group-hover:text-[#0D9488] leading-tight truncate">{n.name}</div>
                      <div className="text-[0.6rem] font-semibold text-[#64748B] mt-0.5">{tr(n.type)} · {n.dist_km} km</div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="space-y-5">
            {place.facts?.length > 0 && (
              <div data-testid="place-facts-card" className="border border-[#BFD4F2] rounded-3xl p-5 bg-white">
                <span className="flex items-center gap-2 font-bold text-sm text-[#1D4ED8]"><Info size={15} /> {tr("Quick facts")}</span>
                <div className="mt-3 space-y-2">
                  {place.facts.map((f) => (
                    <div key={f.label} className="text-xs leading-relaxed">
                      <span className="font-extrabold text-[#0F2557]">{tr(f.label)}: </span>
                      <span className="text-[#475569]">{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="border border-[#FDBA74] rounded-3xl p-5 bg-[#FFF7ED]" data-testid="place-weather-card">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-sm text-[#9A3412]"><ThermometerSun size={16} /> {tr("Weather now")}</span>
                {place.weather?.live && <span className="text-[0.55rem] font-extrabold tracking-widest text-[#15803D] bg-[#F0FDF4] border border-[#C7E8D2] rounded-full px-2 py-0.5">LIVE</span>}
              </div>
              {place.weather ? (
                <div className="mt-3">
                  <div className="text-3xl font-extrabold text-[#0F2557]">{Math.round(place.weather.temp)}°C</div>
                  <div className="text-sm font-semibold text-[#334155]">{place.weather.condition}</div>
                  <div className="text-xs text-[#64748B] mt-1">H {Math.round(place.weather.high)}° / L {Math.round(place.weather.low)}° · {place.weather.humidity}% · {place.weather.wind} km/h</div>
                </div>
              ) : <p className="text-xs text-[#64748B] mt-3">{tr("Weather unavailable right now.")}</p>}
            </div>

            <ShareBadge url={`https://worldpulse.pro/place/${slug}`} />

            <div className="border border-[#D8CCF2] rounded-3xl p-5 bg-[#FAF5FF]">
              <span className="flex items-center gap-2 font-bold text-sm text-[#6D28D9]"><Landmark size={15} /> {tr("Civic Notices")} <em className="not-italic text-[0.55rem] font-extrabold text-[#92400E] bg-[#FFF7ED] border border-[#FDBA74] rounded-full px-2 py-0.5">{tr("DEMO")}</em></span>
              <div className="mt-3 space-y-2 text-xs text-[#334155]">
                {place.civic.slice(0, 2).map((c) => <div key={c.title}><span className="font-bold">{c.title}</span> — {c.body}</div>)}
              </div>
            </div>

            <div className="border border-[#93C5FD] rounded-3xl p-5 bg-[#F6FAFF]">
              <span className="flex items-center gap-2 font-bold text-sm text-[#1D4ED8]"><Plane size={15} /> {tr("Departures")} <em className="not-italic text-[0.55rem] font-extrabold text-[#92400E] bg-[#FFF7ED] border border-[#FDBA74] rounded-full px-2 py-0.5">{tr("DEMO")}</em></span>
              <div className="mt-3 space-y-1.5">
                {place.aviation.slice(0, 3).map((a) => (
                  <div key={a.flight} className="flex justify-between text-xs font-mono-wp text-[#334155]">
                    <span className="font-bold">{a.flight}</span><span className="truncate px-2 flex-1">{a.route}</span><span>{a.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
